import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridRowSelectionModel,
  GridValidRowModel,
} from "@mui/x-data-grid";
import { CrudFilter, useCustom, useGetLocale } from "@refinedev/core";
import { useDataGrid } from "@refinedev/mui";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { trTR, enUS } from "@mui/x-data-grid/locales";
import { Alert, Typography } from "@mui/material";
import { DefaultValues, FormProvider, Path } from "react-hook-form";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { InfoOutlined } from "@mui/icons-material";
import { FilterRow, PaginatedListFilter, Searchable } from "./FilterRow";
import DeleteRow from "./DeleteRow";
import { useForm } from "../../../hooks/useForm";
import { getQuery } from "./getQuery";
import { SyncrosaleLoader } from "../../layout/components/SyncrosaleLoader";

// T is type for the row model, K is type for the filter
type PaginatedListProps<T extends GridValidRowModel, K extends Searchable> = {
  columns: GridColDef<T>[];
  getUrl: (_countryId?: number) => string;
  addButton: React.ReactElement;
  alertText?: string;
  filters?: PaginatedListFilter<K>[];
  defaultFilters?: DefaultValues<K>;
  searchLabel?: string;
  rowHeight?: number;
  searchable?: boolean;
  downloadable?: boolean;
  disableMultipleRowSelection?: boolean;
};

export const PaginatedList = <
  T extends GridValidRowModel,
  K extends Searchable = Searchable,
>(
  props: PaginatedListProps<T, K>,
) => {
  const {
    columns,
    getUrl,
    addButton,
    filters = [],
    defaultFilters,
    searchLabel,
    alertText,
    rowHeight,
    searchable = true,
    downloadable = true,
    disableMultipleRowSelection = false,
  } = props;

  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const locale = useGetLocale();
  const currentLocale = locale();

  const {
    dataGridProps,
    setFilters,
    setCurrent,
    sorters,
    tableQuery: { isInitialLoading },
  } = useDataGrid<T>({
    resource: getUrl(selectedCountry?.id),
    queryOptions: {
      enabled: !!selectedCountry,
    },
    sorters: {
      initial: [
        {
          field: "created",
          order: "desc",
        },
      ],
    },
  });

  const filterFormMethods = useForm<K>({
    defaultValues: defaultFilters ?? ({ search: "" } as DefaultValues<K>),
    warnWhenUnsavedChanges: false,
  });

  const { watch, reset } = filterFormMethods;

  const filterKeys = useMemo(() => {
    const initialFilters = filters.map((f) => f.formKey);
    if (searchable) {
      initialFilters.push("search");
    }
    return initialFilters;
  }, [filters]) as Path<K>[];

  useEffect(() => {
    if (selectedCountry) {
      // Reset form when country changes
      reset();
    }
  }, [selectedCountry]);

  const filterValues = watch(filterKeys);

  const debouncedSetFilters = useCallback(
    // eslint-disable-next-line react-compiler/react-compiler
    debounce((filters: CrudFilter[]) => {
      setFilters(filters);
      setCurrent(1);
    }, 500),
    [],
  );

  useEffect(() => {
    const filter: CrudFilter[] = filterValues.map((value, index) => ({
      field: filterKeys[index] as string,
      operator: "eq",
      value,
    }));

    debouncedSetFilters(filter);
  }, [...filterValues, debouncedSetFilters]);

  const queries = useMemo(() => {
    const query: Record<string, string> = {};

    if (sorters[0]) {
      query.sort = sorters[0].field;
      query.order = sorters[0].order;
    }

    filterKeys.forEach((key, index) => {
      if (filterValues[index]) {
        query[key] = filterValues[index] as string;
      }
    });

    return getQuery(query);
  }, [sorters, filterKeys, filterValues]);

  const { refetch: startDownload, isFetching: isDownloadLoading } = useCustom({
    url: getUrl(selectedCountry?.id) + "/export",
    method: "get",
    config: {
      query: queries,
    },
    queryOptions: {
      enabled: false,
      staleTime: 0,
    },
  });

  if (isInitialLoading) {
    return <SyncrosaleLoader />;
  }

  return (
    <FormProvider {...filterFormMethods}>
      {alertText && (
        <Alert
          sx={{ margin: "8px 0px" }}
          icon={
            <InfoOutlined fontSize="inherit" style={{ alignSelf: "center" }} />
          }
          severity="info"
        >
          <Typography variant="body2">{alertText}</Typography>
        </Alert>
      )}
      {rowSelectionModel && rowSelectionModel.length > 0 ? (
        <DeleteRow
          selectedItems={rowSelectionModel}
          endpoint={getUrl(selectedCountry?.id)}
          resetSelection={() => setRowSelectionModel([])}
          disableMultiple={disableMultipleRowSelection}
        />
      ) : (
        <FilterRow
          downloadLoading={isDownloadLoading}
          onStartDownload={startDownload}
          addButton={addButton}
          filters={filters}
          searchLabel={searchLabel}
          searchable={searchable}
          downloadable={downloadable}
        />
      )}
      <DataGrid
        {...(dataGridProps as unknown as DataGridProps)}
        sx={{ flex: 1 }}
        filterModel={undefined}
        columns={columns}
        localeText={
          (currentLocale === "tr" ? trTR : enUS).components.MuiDataGrid
            .defaultProps.localeText
        }
        disableMultipleRowSelection={disableMultipleRowSelection}
        rowHeight={rowHeight}
        disableColumnFilter
        disableColumnMenu
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        keepNonExistentRowsSelected
        hideFooterSelectedRowCount
      />
    </FormProvider>
  );
};
